import React, {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import classNames from "classnames";

import {
  EditableContent,
  EditableContentInterface,
} from "../EditableContent/EditableContent";

export interface EditableTextInterface extends EditableContentInterface {
  /** Value to display. */
  value?: string | null;
  /** Optional callback called on enter, click outside and tab. */
  onSave?: (e: ChangeEvent<HTMLInputElement>) => void;
  /** Optional callback called on input reset. */
  onCancel?: () => void;
  /** If true, setting empty value will save it, instead of revert to previous value. */
  allowEmptyString?: boolean;
}

export const EditableText = forwardRef<HTMLDivElement, EditableTextInterface>(
  (
    { onSave, onCancel, value, inputProps, allowEmptyString, ...props },
    ref
  ) => {
    const [currentValue, setCurrentValue] = useState(value);
    const [prevValue, setPrevValue] = useState(value);
    const escapeRef = useRef(false);

    useEffect(() => {
      if (currentValue !== value) {
        setCurrentValue(value);
        setPrevValue(value);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const handleBlur = useCallback(
      (e) => {
        if (escapeRef.current) {
          setCurrentValue(prevValue);
          escapeRef.current = false;
        } else {
          if (
            e.target.value.trim().length > 0 &&
            prevValue !== e.target.value
          ) {
            setPrevValue(e.target.value);
            setCurrentValue(e.target.value);
            typeof onSave === "function" && onSave(e);
          } else {
            !allowEmptyString
              ? setCurrentValue(prevValue)
              : typeof onSave === "function" &&
                prevValue !== e.target.value &&
                onSave(e);
          }
        }
      },
      [allowEmptyString, onSave, prevValue]
    );

    const handleKeyDown = useCallback(
      (e) => {
        if (e.key === "Enter") {
          e.target.blur();
        }
        if (e.key === "Escape") {
          escapeRef.current = true;
          e.target.blur();
          typeof onCancel === "function" && onCancel();
        }
      },
      [onCancel]
    );

    const handleChange = useCallback((e) => {
      setCurrentValue(e.target.value);
    }, []);

    return (
      <EditableContent
        {...props}
        ref={ref}
        inputProps={{
          ...inputProps,
          value: currentValue ?? "",
          onBlur: handleBlur,
          onKeyDown: handleKeyDown,
          onChange: handleChange,
          type: "text",
          className: classNames("c-input", inputProps?.className),
        }}
      />
    );
  }
);

EditableText.displayName = "EditableText";
